/**
 * «Видео» — архив камер салонов из Macroscop: салон → день → просмотрщик `VideoStudio` (по умолчанию
 * сетка всех камер салона на 12:00, шкала с зумом, кадры вокруг, ролики по выбранным камерам). Адрес
 * держит выбор (`?location=&at=&camera=&cams=&view=&from=&to=`) — по нему же открывает событие сделки
 * из вкладки «Договор». Право `video_view`. Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSearchParams } from "react-router";

import { type StudioState, VideoStudio } from "@/components/video/VideoStudio";
import { VIDEO_VIEW, useCan } from "@/lib/access";
import { dayOf, hms, todayMsk, videoCitiesQuery } from "@/lib/video";

const control = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";

export function VideoPage() {
  const can = useCan(VIDEO_VIEW);
  const [params, setParams] = useSearchParams();
  const cities = useQuery({ ...videoCitiesQuery(), enabled: can });
  const location = Number(params.get("location")) || cities.data?.[0]?.id || null;
  const at = params.get("at") ?? `${todayMsk()}T12:00:00`;
  const camera = Number(params.get("camera")) || null;
  const cams = (params.get("cams") ?? "").split(",").map(Number).filter(Boolean);
  const view = params.get("view") === "grid" || (!params.get("view") && camera == null) ? "grid" : "one";
  const set = useCallback(
    (next: Record<string, string | undefined>) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(next)) {
            if (v) out.set(k, v);
            else out.delete(k);
          }
          return out;
        },
        { replace: true },
      ),
    [setParams],
  );
  const onStudio = useCallback(
    (v: StudioState) =>
      set({ camera: String(v.camera), cams: v.cams.join(",") || undefined, at: v.at, from: v.from ?? undefined, to: v.to ?? undefined, view: v.view }),
    [set],
  );
  if (!can) return <main className="p-5 text-[13px] text-fg-muted">Раздел «Видео» — по праву «Смотреть камеры салонов».</main>;
  const day = dayOf(at);
  const reset = { camera: undefined, cams: undefined, from: undefined, to: undefined, view: undefined };
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка видео">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Видео</h1>
        <select value={location ?? ""} onChange={(e) => set({ ...reset, location: e.target.value })} className={control} data-annot="выбор салона">
          {cities.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {c.cameras}
            </option>
          ))}
        </select>
        <input type="date" value={day} onChange={(e) => e.target.value && set({ at: `${e.target.value}T${hms(at)}`, from: undefined, to: undefined })} className={control} />
        {cities.isError && <span className="text-[12px] text-err">Салоны не загрузились: {cities.error.message}</span>}
      </div>
      {location != null && (
        <VideoStudio
          key={`${location}-${day}`}
          locationId={location}
          selected={cams}
          camera={camera}
          at={at}
          from={params.get("from")}
          to={params.get("to")}
          view={view}
          autoFocus
          onChange={onStudio}
        />
      )}
    </main>
  );
}
